/**
 * Stable, semantic TanStack Query keys for AI review settings.
 * Avoids null placeholders by carving out explicit hierarchy levels.
 */
export const aiReviewSettingsKeys = {
  all: ['ai-review-settings'] as const,
  installation: (installationId: number) =>
    [...aiReviewSettingsKeys.all, 'installation', installationId] as const,
  repository: (installationId: number, repositoryId: number) =>
    [...aiReviewSettingsKeys.installation(installationId), 'repository', repositoryId] as const,
  repositories: (installationId: number) =>
    [...aiReviewSettingsKeys.installation(installationId), 'repository'] as const,
  installationDefaults: (installationId: number) =>
    [...aiReviewSettingsKeys.installation(installationId), 'defaults'] as const,
  unresolvedSelection: (installationId: number) =>
    [...aiReviewSettingsKeys.installation(installationId), 'unresolved-selection'] as const,
};

export const onboardingAIReviewSettingsKeys = {
  all: [...aiReviewSettingsKeys.all, 'onboarding'] as const,
  installation: (installationId: number) =>
    [...onboardingAIReviewSettingsKeys.all, 'installation', installationId] as const,
  repository: (installationId: number, repositoryId: number) =>
    [
      ...onboardingAIReviewSettingsKeys.installation(installationId),
      'repository',
      repositoryId,
    ] as const,
  allRepositories: (installationId: number) =>
    [...onboardingAIReviewSettingsKeys.installation(installationId), 'all-repositories'] as const,
};

/**
 * Helper used by callers that currently pass nullable IDs.
 * - Missing installation → base key (noop caches)
 * - Undefined repository → unresolved-selection key (disabled queries)
 * - Null repository → installation-level "all repositories" key
 * - Repository present → repository key
 */
export type AIReviewSettingsQueryKeyOptions = {
  /**
   * Keeps fallback state scoped to the repository metadata used when the
   * selection resolved.
   */
  repositoryIsPrivate?: boolean | null;
};

export const aiReviewSettingsQueryKey = (
  installationId: number | null,
  repositoryId: number | null | undefined,
  options?: AIReviewSettingsQueryKeyOptions,
) => {
  if (!installationId) {
    return aiReviewSettingsKeys.all;
  }

  if (repositoryId === null) {
    return aiReviewSettingsKeys.installationDefaults(installationId);
  }

  if (repositoryId === undefined) {
    return aiReviewSettingsKeys.unresolvedSelection(installationId);
  }

  const repositoryKey = aiReviewSettingsKeys.repository(installationId, repositoryId);
  if (options?.repositoryIsPrivate === undefined) {
    return repositoryKey;
  }

  return [...repositoryKey, { repositoryIsPrivate: options.repositoryIsPrivate }] as const;
};

export const onboardingAIReviewSettingsQueryKey = (
  installationId: number | null,
  repositoryId: number | null,
) => {
  if (!installationId) {
    return onboardingAIReviewSettingsKeys.all;
  }

  if (repositoryId === null) {
    return onboardingAIReviewSettingsKeys.allRepositories(installationId);
  }

  return onboardingAIReviewSettingsKeys.repository(installationId, repositoryId);
};
